// TournamentSystem — offer generation, bracket simulation and tournament
// progression. Runs from GameDirector so tournaments advance even when
// the Competitions page is not open.

import { EventBus, GameEvents } from '../utils/EventBus'
import { useGameStore } from '@/store/gameStore'
import { BuildingType } from '@/config/buildingConfig'
import {
  TOURNAMENT_DEFINITIONS,
  Tournament,
  TournamentType,
  Bracket,
  generateTournament,
  canHostTournament,
  calculateTournamentIncome,
  generateNpcTeam,
  generateBracket,
  bracketTeamCount,
  roundsPerDay,
  getBracketChampion,
} from '@/config/tournamentConfig'
import { getEffectiveRating, isAvailable } from '@/config/playersConfig'
import { getDivisionOrNone } from '@/config/federationConfig'
import { PadelTeam, simulateMatch } from './MatchSimulator'
import { Rng, defaultRng } from '../utils/rng'

const OFFER_CHANCE = 0.1
const MAX_PENDING_OFFERS = 2
const OFFER_START_DELAY_DAYS = 3
const CHAMPION_PRIZE_SHARE = 0.3 // club's share of the prize pool if its pair wins
const MATCH_FATIGUE = 10 // fatigue per bracket match played

/** Accept a pending offer; optionally enter the club's two best AVAILABLE
 *  players as a pair (injured players can't compete) */
export function acceptTournamentOffer(type: TournamentType, withClubPair = false) {
  const store = useGameStore.getState()
  const tournament = generateTournament(type, store.day + OFFER_START_DELAY_DAYS)
  tournament.participants = tournament.maxParticipants

  const availablePlayers = store.players.filter(isAvailable)
  if (withClubPair && availablePlayers.length >= 2) {
    const best = [...availablePlayers]
      .sort((a, b) => getEffectiveRating(b) - getEffectiveRating(a))
      .slice(0, 2)
    tournament.clubPairIds = best.map(p => p.id)
  }

  store.setActiveTournament(tournament)
  store.setTournamentOffers(store.tournamentOffers.filter(t => t !== type))
}

export function declineTournamentOffer(type: TournamentType) {
  const store = useGameStore.getState()
  store.setTournamentOffers(store.tournamentOffers.filter(t => t !== type))
}

export class TournamentSystem {
  private handler = () => {
    this.maybeGenerateOffer()
    this.progressActiveTournament()
  }

  constructor(private rng: Rng = defaultRng) {
    EventBus.on(GameEvents.DAY_CHANGED, this.handler)
  }

  destroy() {
    EventBus.off(GameEvents.DAY_CHANGED, this.handler)
  }

  private maybeGenerateOffer() {
    const store = useGameStore.getState()
    if (store.courts.length === 0) return
    if (store.tournamentOffers.length >= MAX_PENDING_OFFERS) return
    if (this.rng() >= OFFER_CHANCE) return

    const types: TournamentType[] = ['amateur', 'intermediate', 'professional', 'championship']
    const eligible = types.filter(type => {
      const def = TOURNAMENT_DEFINITIONS[type]
      const hasBuildingCheck = (t: string) => store.hasBuilding(t as BuildingType)
      const { canHost } = canHostTournament(
        def, store.courts.length, store.reputation, hasBuildingCheck,
        getDivisionOrNone(store.league),
      )
      return canHost && !store.tournamentOffers.includes(type)
    })

    if (eligible.length > 0) {
      const randomType = eligible[Math.floor(this.rng() * eligible.length)]
      store.setTournamentOffers([...store.tournamentOffers, randomType])
    }
  }

  private progressActiveTournament() {
    const store = useGameStore.getState()
    const tournament = store.activeTournament
    if (!tournament) return

    if (tournament.status === 'upcoming' && store.day >= tournament.startDay) {
      const started: Tournament = {
        ...tournament,
        status: 'in_progress',
        bracket: this.buildBracket(tournament),
      }
      store.setActiveTournament(started)
      this.resolveTodaysRounds(started)
    } else if (tournament.status === 'in_progress') {
      this.resolveTodaysRounds(tournament)
    }
  }

  /** Assemble NPC pairs plus the club pair (if entered) and seed the draw */
  private buildBracket(tournament: Tournament): Bracket {
    const teamCount = bracketTeamCount(tournament.participants)
    const teams: PadelTeam[] = []

    const clubTeam = this.buildClubTeam(tournament)
    const npcCount = clubTeam ? teamCount - 1 : teamCount
    for (let i = 0; i < npcCount; i++) {
      teams.push(generateNpcTeam(tournament.type, this.rng))
    }
    if (clubTeam) {
      const position = Math.floor(this.rng() * (teams.length + 1))
      teams.splice(position, 0, clubTeam)
    }

    return generateBracket(teams)
  }

  private buildClubTeam(tournament: Tournament): PadelTeam | null {
    const store = useGameStore.getState()
    if (!tournament.clubPairIds || tournament.clubPairIds.length < 2) return null

    const pair = tournament.clubPairIds
      .map(id => store.players.find(p => p.id === id))
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
    if (pair.length < 2) return null

    return {
      name: `${pair[0].name.split(' ').pop()} / ${pair[1].name.split(' ').pop()}`,
      rating: (pair[0].level + pair[1].level) / 2,
      form: (pair[0].form + pair[1].form) / 2,
      isClubTeam: true,
    }
  }

  /** Resolve as many rounds as fit in one day for this tournament's duration */
  private resolveTodaysRounds(tournament: Tournament) {
    const store = useGameStore.getState()
    if (!tournament.bracket) return

    const bracket: Bracket = {
      ...tournament.bracket,
      matches: tournament.bracket.matches.map(m => ({ ...m })),
    }
    const perDay = roundsPerDay(bracket.totalRounds, tournament.duration)

    for (let i = 0; i < perDay && bracket.currentRound < bracket.totalRounds; i++) {
      this.resolveRound(bracket, tournament)
      bracket.currentRound++
    }

    if (bracket.currentRound >= bracket.totalRounds) {
      this.completeTournament({ ...tournament, bracket })
    } else {
      store.setActiveTournament({ ...tournament, bracket })
    }
  }

  private resolveRound(bracket: Bracket, tournament: Tournament) {
    const round = bracket.currentRound
    const roundMatches = bracket.matches.filter(m => m.round === round)

    for (const match of roundMatches) {
      if (!match.teamA || !match.teamB || match.result) continue

      const result = simulateMatch(match.teamA, match.teamB, { rng: this.rng })
      match.result = result
      const winner = result.winner === 0 ? match.teamA : match.teamB

      // Track club players' stats and form
      const clubInvolved = match.teamA.isClubTeam || match.teamB.isClubTeam
      if (clubInvolved) {
        this.updateClubPairStats(tournament, winner.isClubTeam)
      }

      // Advance winner to the next round
      const next = bracket.matches.find(
        m => m.round === round + 1 && m.slot === Math.floor(match.slot / 2)
      )
      if (next) {
        if (match.slot % 2 === 0) next.teamA = winner
        else next.teamB = winner
      }
    }
  }

  private updateClubPairStats(tournament: Tournament, won: boolean) {
    const store = useGameStore.getState()
    for (const id of tournament.clubPairIds ?? []) {
      const player = store.players.find(p => p.id === id)
      if (!player) continue
      store.updatePlayer(id, {
        matchesPlayed: player.matchesPlayed + 1,
        matchesWon: player.matchesWon + (won ? 1 : 0),
        form: Math.max(0, Math.min(100, player.form + (won ? 3 : -2))),
        fatigue: Math.min(100, (player.fatigue ?? 0) + MATCH_FATIGUE),
      })
    }
  }

  private completeTournament(tournament: Tournament) {
    const store = useGameStore.getState()
    const def = TOURNAMENT_DEFINITIONS[tournament.type]

    const income = calculateTournamentIncome(tournament)
    store.addMoney(income, `Torneo: ${tournament.name}`)

    const champion = tournament.bracket ? getBracketChampion(tournament.bracket) : null
    if (champion?.isClubTeam) {
      const championPrize = Math.floor(tournament.prizePool * CHAMPION_PRIZE_SHARE)
      store.addMoney(championPrize, `Campeones! ${tournament.name}`)
      store.addReputation(def.reputationBonus * 2)
      EventBus.emit(GameEvents.TOURNAMENT_WON, {
        tournamentName: tournament.name,
        prize: championPrize,
      })
      for (const id of tournament.clubPairIds ?? []) {
        const player = store.players.find(p => p.id === id)
        if (player) {
          store.updatePlayer(id, { form: Math.min(100, player.form + 10) })
        }
      }
    } else {
      store.addReputation(def.reputationBonus)
    }

    store.incrementTournamentsCompleted()
    // Keep the finished bracket visible until a new tournament replaces it
    store.setActiveTournament({ ...tournament, status: 'completed' })
  }
}
